// Per-session persistence for the Positions page's Open Orders sort.
//
// The bug this guards against: PositionTracker's `orderSort` was a lazy
// useState seeded ONCE at mount from `defaultPositionsOrderSort` and never
// written back, so an involuntary remount (the reconnect/auth blip that
// unmounts RequireAuth) re-read the factory default and silently dropped the
// trader's live Queue pick (Jun 27 multi-window diagnosis, symptom #4).
//
// This store preserves the trader's ACTUAL current pick — including the live
// `queue`/`velocity` keys that `defaultPositionsOrderSort` deliberately coerces
// away on read (auto-loading those made the positions view "spazz"). Restoring
// them here is correct because they're the trader's explicit per-session choice
// and PositionTracker already throttles those sorts. sessionStorage keeps it
// per-tab and clears on tab close, so it stays a session preference rather than
// a saved default.

export type PositionsOrderSortKey = 'default' | 'size' | 'queue' | 'velocity';
export type PositionsOrderSortDir = 'asc' | 'desc';

export interface PositionsOrderSort {
  key: PositionsOrderSortKey;
  dir: PositionsOrderSortDir;
}

const SESSION_KEY = 'g1mt_positions_order_sort_session';
const VALID_KEYS: ReadonlySet<PositionsOrderSortKey> = new Set<PositionsOrderSortKey>([
  'default',
  'size',
  'queue',
  'velocity',
]);

/** Read the per-session Open Orders sort, or null if absent/corrupt/invalid
 *  (callers fall back to the landing default). Any malformed blob, unknown
 *  key, or storage failure resolves to null — never throws. */
export function readSessionOrderSort(): PositionsOrderSort | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { key?: unknown; dir?: unknown };
    if (typeof parsed.key !== 'string' || !VALID_KEYS.has(parsed.key as PositionsOrderSortKey)) {
      return null;
    }
    const dir: PositionsOrderSortDir = parsed.dir === 'desc' ? 'desc' : 'asc';
    return { key: parsed.key as PositionsOrderSortKey, dir };
  } catch {
    return null;
  }
}

export function writeSessionOrderSort(sort: PositionsOrderSort): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(sort));
  } catch {
    // sessionStorage full/unavailable — the sort just won't survive a remount.
  }
}
